import { useRef, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Modal, useToast } from '../../design-system';
import '../styles/ImportClients.css';

// Download-template -> fill in Excel -> upload -> partial-success report.
//
// Generalised from ImportClientsModal, which is where this shape was proven, so
// every import in the app reads the same way: the same three steps, the same
// result panel, the same "fix these rows and upload again" ending. A firm that
// has imported clients already knows how to import DSCs.
//
// Props:
//   basePath     e.g. '/api/dscs' — GET {basePath}/import/template, POST {basePath}/import
//   title        modal heading
//   noun/nounPlural  what a row is ("DSC" / "DSCs"), used throughout the copy
//   templateFile the download filename
//   describeTemplate / describeUpload  the step 1 / step 2 body text
//   rowLabel     (row) => string — what to call a failed row in the report;
//                each import returns a different identifying field
//   warning      optional red banner shown above step 1 (the credential import
//                uses it to say the file holds plaintext passwords)
//   onImported   called after any successful row, so the page can refetch
function ImportModal({
  isOpen,
  onClose,
  onImported,
  basePath,
  title,
  noun = 'row',
  nounPlural = 'rows',
  templateFile = 'import-template.xlsx',
  describeTemplate,
  describeUpload,
  rowLabel = (r) => r.label || r.title || r.displayName || r.fileNumber || r.dscCode || '',
  warning = null,
}) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get(`${basePath}/import/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFile;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.danger('Failed to download the template');
    } finally {
      setDownloading(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true);
    setError('');
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post(`${basePath}/import`, form);
      setResult(res.data);
      const done = (res.data.createdCount || 0) + (res.data.updatedCount || 0);
      if (done > 0) {
        toast.success(`Imported ${done} of ${res.data.totalRows} ${res.data.totalRows === 1 ? noun : nounPlural}`);
        onImported?.();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import this file');
    } finally {
      setImporting(false);
      // The chosen file is dropped from the input either way. It matters most
      // for the credential import, where it is a spreadsheet of plaintext
      // passwords and there is no reason to keep it selected in the page.
      if (fileRef.current) fileRef.current.value = '';
      setFile(null);
    }
  };

  const imported = result ? (result.createdCount || 0) + (result.updatedCount || 0) : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title}
      size="lg"
      footer={<Button variant="secondary" onClick={close}>{result ? 'Done' : 'Cancel'}</Button>}
    >
      <div className="import-clients">
        {warning && <div className="import-warning" role="alert">{warning}</div>}

        {!result && (
          <>
            <div className="import-step">
              <div className="import-step-n">1</div>
              <div className="import-step-body">
                <p><strong>Download the template</strong></p>
                <p className="hint-text">{describeTemplate}</p>
                <Button variant="secondary" size="sm" onClick={downloadTemplate} loading={downloading}>
                  ⇩ Download template (.xlsx)
                </Button>
              </div>
            </div>

            <div className="import-step">
              <div className="import-step-n">2</div>
              <div className="import-step-body">
                <p><strong>Fill it in and upload it back</strong></p>
                <p className="hint-text">{describeUpload}</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="ds-input"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setError(''); }}
                />
              </div>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            <div className="import-actions">
              <Button variant="primary" onClick={runImport} loading={importing} disabled={!file}>
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </>
        )}

        {result && (
          <div className="import-result">
            <div className="import-summary">
              <span className="import-summary-ok">{result.createdCount} created</span>
              {/* Only the upserting imports (DSC) report updates; the rest omit
                  the field entirely rather than showing a permanent zero. */}
              {result.updatedCount > 0 && <span className="import-summary-ok">{result.updatedCount} updated</span>}
              {result.errorCount > 0 && <span className="import-summary-bad">{result.errorCount} failed</span>}
              <span className="hint-text">of {result.totalRows} row{result.totalRows === 1 ? '' : 's'}</span>
            </div>

            {result.errors?.length > 0 && (
              <div className="import-errors">
                <p className="ds-field-label">What went wrong</p>
                <div className="import-errors-list">
                  {result.errors.map((e) => (
                    <div key={e.row} className="import-error-row">
                      <span className="import-error-row-n">Row {e.row}</span>
                      <span className="import-error-row-name">{rowLabel(e)}</span>
                      <span className="import-error-row-msg">{e.error}</span>
                    </div>
                  ))}
                </div>
                <p className="hint-text">
                  Fix these rows in your spreadsheet and upload it again — delete the rows that
                  already succeeded first, so they aren&apos;t created twice.
                </p>
              </div>
            )}

            {imported > 0 && (
              <Button variant="secondary" size="sm" onClick={reset}>Import another file</Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ImportModal;
